import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { Vendor } from '../types';
import { fetchAuthSession } from 'aws-amplify/auth';

interface BusinessContextType {
  selectedVendorId: string | null;
  vendors: Vendor[];
  loading: boolean;
  error: string | null;
  switchBusiness: (vendorId: string) => void;
  refreshVendors: () => Promise<void>;
  hasNoBusinesses: boolean;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

interface BusinessProviderProps {
  children: ReactNode;
}

export const BusinessProvider: React.FC<BusinessProviderProps> = ({ children }) => {
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(() => {
    // Initialize from storage immediately to prevent flicker
    return sessionStorage.getItem('selectedVendorId') || localStorage.getItem('selectedVendorId');
  });
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNoBusinesses, setHasNoBusinesses] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Monitor auth state
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await fetchAuthSession();
        if (session.tokens?.idToken) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          setLoading(false);
        }
      } catch (e) {
        setIsAuthenticated(false);
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Load vendors and initialize selection when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const initBusiness = async () => {
      try {
        setLoading(true);
        console.log('[BUSINESS-CTX] Loading vendors...');
        const vendorList = await apiService.listMyVendors();
        setVendors(vendorList);

        if (vendorList.length === 0) {
          console.log('[BUSINESS-CTX] No businesses found');
          setHasNoBusinesses(true);
          setSelectedVendorId(null);
          // Clear storage to prevent stale state
          sessionStorage.removeItem('selectedVendorId');
          localStorage.removeItem('selectedVendorId');
        } else {
          setHasNoBusinesses(false);
          
          // Determine which vendor to select
          let targetVendorId = selectedVendorId; // Currently local state (from storage init)

          // Validate if the stored vendor still exists in the list
          const vendorExists = targetVendorId && vendorList.some(v => v.vendorId === targetVendorId);

          if (!targetVendorId || !vendorExists) {
            console.log('[BUSINESS-CTX] No valid local selection, checking API preference...');
            
            // Try API preference
            try {
              const prefs = await apiService.getUserPreferences();
              if (prefs?.selectedVendorId && vendorList.some(v => v.vendorId === prefs.selectedVendorId)) {
                targetVendorId = prefs.selectedVendorId;
                console.log('[BUSINESS-CTX] Using API preference:', targetVendorId);
              } else {
                // Fallback to first vendor
                targetVendorId = vendorList[0].vendorId;
                console.log('[BUSINESS-CTX] Using fallback (first vendor):', targetVendorId);
              }
            } catch (err) {
              console.warn('[BUSINESS-CTX] Failed to fetch preferences, using fallback');
              targetVendorId = vendorList[0].vendorId;
            }
          } else {
            console.log('[BUSINESS-CTX] Valid local selection found:', targetVendorId);
          }

          // Apply the selection
          if (targetVendorId && targetVendorId !== selectedVendorId) {
            setSelectedVendorId(targetVendorId);
            sessionStorage.setItem('selectedVendorId', targetVendorId);
            localStorage.setItem('selectedVendorId', targetVendorId);
            
            // Sync back to API if it wasn't the API preference
            // Fire and forget
            apiService.updateUserPreferences({ selectedVendorId: targetVendorId }).catch(console.warn);
          }
        }
      } catch (err: any) {
        console.error('[BUSINESS-CTX] Error initializing business:', err);
        setError(err.message || 'Failed to load businesses');
      } finally {
        setLoading(false);
      }
    };

    initBusiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const switchBusiness = (vendorId: string) => {
    console.log('[BUSINESS-CTX] Switching business to:', vendorId);
    
    // 1. Update local state
    setSelectedVendorId(vendorId);
    
    // 2. Update storage (Source of truth for next reload)
    sessionStorage.setItem('selectedVendorId', vendorId);
    localStorage.setItem('selectedVendorId', vendorId);
    
    // 3. Update API preference (Fire and forget, but important)
    // We don't await this because we want to reload immediately for better UX
    // The next load will prioritize local storage anyway
    apiService.updateUserPreferences({ selectedVendorId: vendorId })
      .catch(err => console.warn('[BUSINESS-CTX] Failed to sync preference on switch:', err));
    
    // 4. Reload page to apply full context switch
    // Using a small timeout to ensure storage flush (though usually sync)
    setTimeout(() => {
      window.location.reload();
    }, 50);
  };

  const refreshVendors = async () => {
    if (!isAuthenticated) return;
    const vendorList = await apiService.listMyVendors();
    setVendors(vendorList);
  };

  return (
    <BusinessContext.Provider
      value={{
        selectedVendorId,
        vendors,
        loading,
        error,
        switchBusiness,
        refreshVendors,
        hasNoBusinesses
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
};
